// client/src/utils/vendorForecast.js
//
// Pure, shared vendor-forecast synthesis. NO engine / server / React dependency.
//
// ★ Item 8b: ERR_NO_IS_LINE lives HERE — a neutral module both the Vendor
// Dashboard (which THROWS it, in persistVendor / saveWizardVendors) and the
// Forecast Wizard (which READS it, to show the named cause) already import.
// Putting it in VendorDashboard would make the wizard import its own parent —
// a circular import for a one-line constant. ONE constant, two consumers.
//
// ONE code path for "monthly_amount + frequency + billing anchor + renewals +
// start/end  →  forecast vendor_monthly_actuals rows", consumed by:
//   (1) the Vendor Modal save                (VendorDashboard.js, live)
//   (2) the bulk Forecast Wizard             (cat-1 Phase 2)
//   (3) the Session-6 onboarding import fix  (closes "onboarded ≠ wired" for vendor opex)
//
// The engine (computeVendor.js, constant path) reads monthlyData[m] per forecast
// month with NO smoothing assumption, so functional frequency is a pure synthesis
// concern: an annual vendor books its WHOLE unit amount in the billing month and
// $0 the others (NOT amount ÷ 12).

const FREQ_STEP = { monthly: 1, quarterly: 3, semi_annual: 6, annual: 12 };

// 'YYYY-MM' → absolute month index (year*12 + month-1). Sortable / diffable.
const monthIndex = ym => {
  const [y, m] = String(ym).split("-").map(Number);
  return y * 12 + (m - 1);
};

// Field readers tolerant of BOTH the DB-snake shape (start_date, monthly_amount,
// billing_frequency) AND the modal/wizard-camel shape (startDate, amount, freq).
// Mirrors computeVendor.js's `v.forecast_type || v.type` dual-read convention so
// the same helper is byte-identical whether called from the modal or the wizard.
const readAmount   = v => Number(v.amount != null ? v.amount : v.monthly_amount) || 0;
const readRenewals = v => v.renewals || [];
const readStart    = v => v.start_date || v.startDate || null;
const readEnd      = v => v.end_date || v.endDate || "until_canceled";
const readFreq     = v => v.billing_frequency || v.freq || "monthly";
const readAnchor   = v => v.billingAnchor || v.billing_anchor || null;

// ★ Item 8b — the "vendor has no IS line" refuse code. Thrown (as `err.code`) by
// the Vendor Dashboard's persistVendor / saveWizardVendors; read by the Vendor
// Modal's inline block and the Forecast Wizard's note so each can show the NAMED
// cause rather than a generic "save failed". A code, not message-matching.
export const ERR_NO_IS_LINE = 'vendor_no_is_line';

/* ★★ item 8b — assertVendorRouted(vendors[, opts]): THE ONE "is this vendor routed?"
 * predicate, and the ONE refusal message shape. Throws ERR_NO_IS_LINE naming every
 * offender, or returns silently.
 *
 * THE DEFECT IT CLOSES: this check existed ONLY on the Vendor Dashboard
 * (saveWizardVendors' inline `filter(v => !v.isCat)`). The onboarding Step-6 editor
 * had no equivalent, and its adapter can legitimately produce a null route
 * (`isCat: byGl[v.gl_category] || v.is_line || null`) — so the guard was missing
 * exactly where a tenant is mid-onboarding and MOST likely to have an unrouted
 * vendor. An unrouted vendor is not a cosmetic problem: computeVendor keys spend on
 * `is_line`, so it books $0 forever, silently (§12.5 B3-1 / B3-12, and pre-flight P5
 * catches it only later, after the data is already written).
 *
 * ★ ONE PREDICATE, NOT TWO. Both hosts hand in objects that already name the field
 * `isCat` — the Dashboard maps it at VendorDashboard.js:1293 (`isCat: v.is_line || ""`)
 * and Step 6 at its `step6ForecastVendorRows` adapter — so neither caller needs a
 * shim and the predicate never forks. `!v.isCat` catches null, undefined AND the
 * Dashboard's empty-string form, which is why it stays a truthiness test rather than
 * a `== null` check.
 *
 * ★ WHY `opts` EXISTS, AND WHY IT IS ONLY THE PROSE: the offender list, the count,
 * the +N-more truncation, the error code and the sentence structure are FIXED. Only
 * the operator's REMEDY differs, because the two hosts fix this on different screens
 * — telling an onboarding operator to "set an IS line on the Vendor Dashboard" names
 * a page they cannot reach yet. Defaults reproduce the Dashboard's existing message
 * BYTE-FOR-BYTE, so the extraction is a no-op there.
 *   opts.remedy — the fix instruction (default: the Dashboard's)
 *   opts.action — past-tense verb for what did not happen (default 'saved';
 *                 Step 6 passes 'applied', whose button says Apply, not Save) */
/* ★★ vendorRoute(v, catByGl) — THE ONE "where does this vendor's spend book?" DERIVATION.
 *
 * Three surfaces used to answer this question three different ways over the same vendor:
 *   VendorDashboard.js:1293   isCat = v.is_line || ""
 *   ModelProvider.js:322      isCat = v.is_line || null      (what the ENGINE books on)
 *   AdminPage.js:8054         isCat = byGl[v.gl_category] || v.is_line || null
 * and the Step-6 IMPORT asked a fourth (is the category's key a real leaf?) while IGNORING
 * v.is_line entirely. MEASURED on cid=3: 229/229 routed to the Dashboard and to the engine,
 * 0/229 to both Step-6 surfaces — the same vendors, $10,687,610.65 of spend, two answers.
 *
 * ★ THE RULE, and the reason for it: `vendors.is_line` is AUTHORITATIVE, because it is the
 * only stored per-vendor route and it is exactly what computeVendor books on
 * (computeVendor.js:209, `isLines[v.isCat][m] += amt`). The GL-category map is the operator's
 * BULK INPUT that produces it — one level upstream, per category rather than per vendor.
 * So on a re-import the SHEET wins... EXCEPT for a vendor a human hand-edited on the
 * Dashboard, which keeps its line. `is_line_edited_at` is that fact, stamped server-side at
 * edit time (see the vendors PUT); NULL means the import wrote it and may overwrite it.
 *
 * ⛔ THIS IS NOT `assertVendorRouted`'s JOB. That function answers "is this routed AT ALL?"
 * and keeps its signature and message unchanged. The defect was never the predicate — it was
 * that each caller computed a DIFFERENT `isCat` and handed it to the same test. One owner for
 * a derivation is necessary and not sufficient; the callers must also agree on the input.
 *
 * Pure. Returns a leaf id or null; never throws, never books, never writes.
 *   v         a vendor-ish row: { is_line?, is_line_edited_at?, gl_category? }
 *   catByGl   { [gl_category]: { is_line_key } } — the Step-6 category map, or {} when the
 *             caller has none (the Dashboard and the engine have no categories at all, and
 *             for them this reduces to `v.is_line`, which is why they need no change). */
export function vendorRoute(v, catByGl) {
  if (!v) return null;
  const stored = v.is_line || null;
  const handEdited = !!v.is_line_edited_at;
  if (handEdited && stored) return stored;          // the operator's own choice wins
  const catKey = ((catByGl || {})[v.gl_category] || {}).is_line_key || null;
  return catKey || stored;                          // the sheet/category, else what is stored
}

/* vendorRoutePreserved(v, catByGl) — true iff vendorRoute DEVIATED from the category map
 * because this vendor was hand-edited. This is what the Step-6 notice lists, and it is
 * deliberately a SEPARATE function rather than a second return value: the route is consumed
 * in three places that do not care why, and only one surface reports it. */
export function vendorRoutePreserved(v, catByGl) {
  if (!v || !v.is_line_edited_at || !v.is_line) return false;
  const catKey = ((catByGl || {})[v.gl_category] || {}).is_line_key || null;
  return !!catKey && catKey !== v.is_line;
}

export function assertVendorRouted(vendors, opts = {}) {
  const unrouted = (vendors || []).filter(v => !v.isCat);
  if (!unrouted.length) return;
  const action = opts.action || 'saved';
  const remedy = opts.remedy || 'set an IS line on the Vendor Dashboard first';
  const names = unrouted.map(v => v.name || `#${v.id}`);
  const err = new Error(
    `${names.length} vendor${names.length === 1 ? '' : 's'} ha${names.length === 1 ? 's' : 've'} no income-statement line: ` +
    `${names.slice(0, 5).join(', ')}${names.length > 5 ? `, +${names.length - 5} more` : ''}. ` +
    `Nothing was ${action} — ${remedy} (a vendor with no IS line books $0 spend, silently).`
  );
  err.code = ERR_NO_IS_LINE;
  throw err;
}

// deriveAnchorCalMonth(vendor[, beforeMonth]) → calendar month 1-12 of the
// vendor's LATEST nonzero historical payment, or null if no nonzero history.
//   beforeMonth (optional 'YYYY-MM'): consider only months strictly before it
//   (the historical window). Omitted → consider every month in monthlyData.
// Pure, no side effects.
export function deriveAnchorCalMonth(vendor, beforeMonth) {
  const md = vendor && vendor.monthlyData;
  if (!md) return null;
  let latest = null;
  for (const m of Object.keys(md)) {
    if (beforeMonth && !(m < beforeMonth)) continue; // historical-only window
    if (!md[m]) continue;                            // skip zero / falsy
    if (latest === null || m > latest) latest = m;
  }
  if (latest === null) return null;
  return Number(latest.split("-")[1]); // MM as 1-12
}

// synthesizeForecastVmas(vendor, forecastMonths[, opts]) → [{ month, amount }]
//
// Returns one row for EVERY forecastMonth (no month left undefined):
//   - booked months  → the WHOLE unit amount (renewals applied), NOT amount÷step
//   - off-cadence / out-of-range months → explicit 0 (clears stale rows, keeps
//     the engine at $0 there).
//
// step   = FREQ_STEP[billing_frequency]  (default monthly / step 1).
// anchor = the first forecast billing month:
//            explicit override (vendor.billingAnchor / opts.anchor)
//            ?? for step>1: first forecastMonth whose calendar-month ===
//               deriveAnchorCalMonth(vendor)   (derived from history)
//            ?? forecastMonths[0]               (no-history / monthly fallback)
//   For step===1 (monthly — every constant vendor today) the derived anchor is
//   intentionally NOT applied: anchor=forecastMonths[0] so every in-range month
//   books, byte-identical to the legacy modal loop.
// getAmt(m), inRange(m): the modal's exact renewal + start/end logic.
export function synthesizeForecastVmas(vendor, forecastMonths, opts = {}) {
  if (!Array.isArray(forecastMonths) || forecastMonths.length === 0) return [];

  const step     = FREQ_STEP[readFreq(vendor)] || 1;
  const start    = readStart(vendor) || opts.startFallback || forecastMonths[0];
  const end      = readEnd(vendor);
  const renewals = readRenewals(vendor);
  const baseAmt  = readAmount(vendor);

  // Effective amount for month m, considering renewals (modal :647-653 logic).
  const getAmt = m => {
    let amt = baseAmt;
    for (const r of renewals) { if (m >= r.month) amt = r.amount; }
    return amt;
  };
  // In service window (modal :644 logic).
  const inRange = m => m >= start && (end === "until_canceled" || m <= end);

  // Resolve the first forecast billing month (anchor).
  let anchor = opts.anchor || readAnchor(vendor);
  if (!anchor) {
    if (step === 1) {
      anchor = forecastMonths[0]; // monthly: no derived anchor (every in-range month books)
    } else {
      const calMM = deriveAnchorCalMonth(vendor, forecastMonths[0]);
      anchor = (calMM != null
        ? forecastMonths.find(m => Number(m.split("-")[1]) === calMM)
        : null) || forecastMonths[0];
    }
  }
  const anchorIdx = monthIndex(anchor);

  return forecastMonths.map(m => {
    // On cadence = exact multiple of `step` from the anchor, at-or-after it.
    // (m >= anchor guards against booking backward; JS negative-modulo would
    //  otherwise let a month exactly `step` before the anchor slip through.)
    const onCadence = (monthIndex(m) - anchorIdx) % step === 0 && m >= anchor;
    const booked = inRange(m) && onCadence;
    return { month: m, amount: booked ? getAmt(m) : 0 };
  });
}

// buildVendorForecastMd(vendor, actualsMonths, forecastMonths[, opts]) → the
// vendor's full monthlyData object (historical-preserve + type-appropriate
// forecast), shared by the Vendor Modal save AND the bulk Forecast Wizard.
//   - constant → historical actuals + synthesized forecast rows (frequency-aware).
//   - pct_revenue / per_seat / prepaid → historical-preserve ONLY (no forecast
//     vmas; the engine computes their forecast from config / contracts).
// one_off is NOT handled here — its per-month manual amounts are built by the
// caller (the modal's oneOffEntries); the wizard writes one_off config-only.
export function buildVendorForecastMd(vendor, actualsMonths, forecastMonths, opts = {}) {
  const src = vendor.monthlyData || {};
  const md = {};
  actualsMonths.forEach(m => { if (src[m]) md[m] = src[m]; });
  if ((vendor.type || vendor.forecast_type) === "constant") {
    synthesizeForecastVmas(vendor, forecastMonths, opts).forEach(({ month, amount }) => { md[month] = amount; });
  }
  return md;
}
